import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';
import {
  useCGList,
  useCGStatistics,
  useClientFees,
  useOrganizationsList,
  usePersonsList,
  useStages,
} from '../hooks/useApiQueries';
import { useAuthRedirect } from '../hooks/useAuthRedirect';
import useSearchHandler from '../hooks/useSearchHandler';
import theme from '../theme';
import { formatDateToDDMMYYYY } from '../utils';
import { useAuth } from './AuthContext';
import AgencyCGList from './CGList/AgencyCGList';
import { ComplementBadge } from './CGList/Complement';
import CGStageModal from './CGList/CGStageModal';
import PaymentModal from './CGList/PaymentModal';
import CommentsManager from './Comments/CommentsManager';
import SubmitCTVO from './DealsList/DealStageActions/SubmitCTVO';
import SubmitCessionCertificate from './DealsList/DealStageActions/SubmitCessionCertificate';

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const SortIcon = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: theme.spacing(1),
  color: 'white',
}));

// Onglets god view ISO avec la modale CGStageModal et les blocs AgencyCGList.
// Le filtrage backend se fait directement sur la colonne canonique cg_status
// (paramètre `cg_status` de l'API /api/cg, géré par cgController.getCGListController).
//
// Ordre des onglets aligné sur la modale :
//   0: Tous            (pas de filtre)
//   1: En attente paiement     (awaiting_payment)
//   2: Paiement + pièces       (awaiting_docs_and_payment)
//   3: Pièces à déposer        (awaiting_docs)
//   4: Pièces demandées par l'IA (pas un statut : filtre sur l'origine des
//      demandes ouvertes, cf. AI_REQUESTS_TAB_INDEX)
//   5: Réponse attendue        (awaiting_reply)
//   6: En review Skipcar       (awaiting_review)
//   7: Démarches ANTS en cours (awaiting_misiv)
//   8: Procédure effectuée     (completed)
//   9: Rejetées                (rejected)
//  10: Supprimées              (DELETED_TAB_INDEX, queryNonVisible)
const getCgStatusFilter = (tabIndex: number): string | null => {
  switch (tabIndex) {
    case 1:
      return 'awaiting_payment';
    case 2:
      return 'awaiting_docs_and_payment';
    case 3:
      return 'awaiting_docs';
    // 4 : « Pièces demandées par l'IA » ne filtre pas sur un statut mais sur
    // l'origine des demandes ouvertes — un même dossier peut porter les deux.
    case 5:
      return 'awaiting_reply';
    case 6:
      return 'awaiting_review';
    case 7:
      return 'awaiting_misiv';
    case 8:
      return 'completed';
    case 9:
      return 'rejected';
    default:
      return null;
  }
};

const DELETED_TAB_INDEX = 10;

/**
 * « Pièces demandées par l'IA » — demande de François au point du 08/09/2026 :
 * « il va falloir une partie où c'est pièce à déposer demandé par l'IA, pour
 * qu'il soit isolé et que nous on puisse les identifier directement ».
 *
 * Ce n'est pas un statut : ces dossiers vivent dans « Pièces à déposer ».
 * L'onglet filtre sur l'ORIGINE des demandes ouvertes, ce qui le rend
 * volontairement transverse. Les dossiers impayés en sont exclus côté serveur :
 * ils restent dans « En attente de paiement » / « Paiement + pièces » (retour
 * François du 02/10/2026).
 * Un dossier qui porte à la fois une demande de l'IA et une de Skipcar apparaît
 * dans les deux onglets : c'est exact, il attend bien les deux.
 */
const AI_REQUESTS_TAB_INDEX = 4;

/**
 * Onglet d'atterrissage du relecteur god : « En review Skipcar ».
 *
 * Constante et non littéral : le défaut était codé en dur à `4` avec le
 * commentaire « Default tab = 4 (En Review Skipcar) », vrai jusqu'à
 * l'insertion de « Pièces demandées par l'IA » EN POSITION 4 — qui a décalé
 * la review à 6 sans que le défaut suive. Le pôle CG atterrissait donc sur
 * l'onglet IA (retour François 09/09/2026 : « on arrive sur l'onglet pièces
 * demandées par l'IA et plus en Review Skipcar, ce qui est dommage »).
 */
const REVIEW_TAB_INDEX = 6;

// Composant pour afficher le prix avec les frais dynamiques
const CGPriceCell = ({
  clientId,
  taxDataEstim,
  taxData,
}: {
  clientId: string | null | undefined;
  taxDataEstim: any;
  taxData: any;
}) => {
  const { data: clientFeesData, isLoading } = useClientFees(clientId);

  // Récupérer la commission Skipcar (cgFee) depuis l'API ou utiliser 10€ par défaut
  const skipcarFee = (clientFeesData as any)?.data?.cgFee ?? 10;

  // Calculer la couleur de la cellule
  const priceCellColor =
    taxDataEstim?.taxeTotale === undefined || taxData?.TaxeTotale === undefined
      ? 'black'
      : taxDataEstim?.taxeTotale === taxData?.TaxeTotale
        ? 'green'
        : 'red';

  if (isLoading) {
    return (
      <Box sx={{ color: priceCellColor, fontWeight: 600 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box sx={{ color: priceCellColor, fontWeight: 600 }}>
      {taxDataEstim?.taxeTotale && <Box>Estimé : {(taxDataEstim.taxeTotale + skipcarFee * 1.2).toFixed(2)} € TTC</Box>}
      {taxData?.TaxeTotale && <Box>Final : {(taxData.TaxeTotale + skipcarFee * 1.2).toFixed(2)} € TTC</Box>}
    </Box>
  );
};

// Composant pour afficher le prix à payer pour les agences
const CGAgencyPrice = ({ clientId, taxDataEstim }: { clientId: string | null | undefined; taxDataEstim: any }) => {
  const { data: clientFeesData, isLoading } = useClientFees(clientId);

  // Récupérer la commission Skipcar (cgFee) depuis l'API ou utiliser 10€ par défaut
  const skipcarFee = (clientFeesData as any)?.data?.cgFee ?? 10;

  if (isLoading) {
    return <CircularProgress size={16} />;
  }

  // La taxe carte grise est exonérée de TVA (0%), seule la commission Skipcar est soumise à 20% de TVA
  const priceToPayTTC = (taxDataEstim?.taxeTotale ?? 0) + skipcarFee * 1.2;

  return <>{priceToPayTTC.toFixed(2)}</>;
};

const mapSortColumnToQuery = (column: string): string => {
  const columnMap: { [key: string]: string } = {
    'R.RegistrationID': 'R.RegistrationID',
    'R.CreatedAt': 'R.CreatedAt',
    'R.UpdatedAt': 'R.UpdatedAt',
    'R.StageID': 'R.StageID',
    // Add any other column mappings here
    // Default to RegistrationID if not found
  };
  return columnMap[column] || 'R.RegistrationID';
};

const CGList = () => {
  const { currentUser } = useAuth();
  const { redirectToLogin } = useAuthRedirect();
  const { t } = useTranslation('common');
  const userRole = currentUser?.data?.data?.role;
  const restrictedUserId = 'EJBJf06e4WarfF1ruTt3bb6uHE42'; // François peut regénérer les factures
  const userPipedrive = currentUser?.data?.data?.idPipedrive ?? 188;

  // Si l'utilisateur est une agence, afficher la vue agence avec les tableaux colorés
  if (userRole === 'agency') {
    return (
      <Container
        maxWidth={false}
        sx={{ padding: theme.spacing(4), backgroundColor: theme.palette.background.default, paddingTop: 0 }}
      >
        <Grid
          container
          spacing={4}
          sx={{
            marginTop: theme.spacing(0),
            width: '100%',
            maxWidth: 1300,
            mx: 'auto',
            marginBottom: theme.spacing(0),
          }}
        >
          <Grid item xs={12} sx={{ marginTop: 0 }}>
            <AgencyCGList orgId={userPipedrive} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  const [isLoadingInvoice, setIsLoadingInvoice] = useState<boolean>(false);
  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  // Atterrissage sur « En review Skipcar » : le relecteur god arrive directement
  // sur les dossiers à traiter (retour François 2026-07). Les onglets « En attente
  // de paiement » / « Paiement + pièces » restent accessibles en un clic ; on n'y
  // atterrit plus par défaut (ancien default 2 → puis 0 « Tous »).
  // ⚠️ Toujours passer par la constante : un onglet inséré avant la review décale
  // l'indice, et un littéral ici enverrait le pôle CG sur le mauvais onglet.
  const [selectedTab, setSelectedTab] = useState<number>(REVIEW_TAB_INDEX);
  const [sortColumn, setSortColumn] = useState<string>('R.RegistrationID');
  const [sortOrder, setSortOrder] = useState<string>('DESC');
  const [isDeleting, setIsDeleting] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreTargetId, setRestoreTargetId] = useState<number | null>(null);

  const { searchQuery, hasSearched, isAutoSearchLoading, setIsAutoSearchLoading, handleSearch, resetSearch } =
    useSearchHandler<QueryObserverResult>();
  const autoSearchDone = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const openExternalCommentsId = searchParams.get('openExternalComments');
  const autoSearchRegPlate = searchParams.get('autoSearch');

  const { data: stages, isLoading: isLoadingStages } = useStages();

  // Vérifier l'authentification et rediriger si nécessaire
  useEffect(() => {
    if (!currentUser && !isLoadingStages) {
      redirectToLogin();
    }
  }, [currentUser, isLoadingStages, redirectToLogin]);

  const isAiRequestsTab = !searchQuery && selectedTab === AI_REQUESTS_TAB_INDEX;

  // L'onglet « Pièces demandées par l'IA » ne liste plus que des dossiers
  // payés : le serveur écarte les impayés dès que l'origine vaut `analysis`
  // (retour François du 02/10/2026). Le sous-filtre « Pièces seules /
  // Paiement en attente » qui servait à les trier n'a donc plus d'objet.

  // Filtre canonique cg_status pour les onglets god — remplace stageId+godCommentFilter
  // legacy. Aligné sur la modale CGStageModal et les blocs AgencyCGList.
  const cgStatusFilter = searchQuery || isAiRequestsTab ? null : getCgStatusFilter(selectedTab);

  // Origine des demandes de pièces, utile seulement sur l'onglet « Pièces à
  // déposer » : ailleurs il n'y a pas de demande ouverte à trier. Remis à zéro
  // quand on change d'onglet, sinon un filtre invisible s'appliquerait.
  // ⚠️ `!isAiRequestsTab` : sans cette garde, choisir « Pièces seules » dans
  // l'onglet IA y ferait aussi apparaître le sélecteur d'origine, alors que
  // l'onglet impose déjà `analysis`.
  const [docOriginFilter, setDocOriginFilter] = useState<'skipcar' | 'analysis' | null>(null);
  const isDocsTab = !isAiRequestsTab && cgStatusFilter === 'awaiting_docs';
  useEffect(() => {
    if (!isDocsTab && docOriginFilter !== null) setDocOriginFilter(null);
  }, [isDocsTab, docOriginFilter]);
  // L'onglet dédié impose son origine ; ailleurs c'est le sélecteur qui décide.
  const docOriginParam = isAiRequestsTab ? 'analysis' : isDocsTab ? docOriginFilter : null;

  const { data, isLoading, error, refetch } = useCGList(
    userPipedrive,
    userRole,
    page + 1,
    rowsPerPage,
    searchQuery,
    null, // stageId = null : on filtre via cg_status à la place
    mapSortColumnToQuery(sortColumn),
    sortOrder,
    false,
    null, // godCommentFilter legacy = null (les onglets v2 n'en ont plus besoin)
    cgStatusFilter,
    docOriginParam,
  );

  const { data: dataHidden } = useCGList(
    userPipedrive,
    userRole,
    page + 1,
    rowsPerPage,
    searchQuery,
    null,
    mapSortColumnToQuery(sortColumn),
    sortOrder,
    true,
    null,
    cgStatusFilter,
    docOriginParam,
  );

  const cgListData = selectedTab === DELETED_TAB_INDEX ? dataHidden?.cgList || [] : data?.cgList || [];
  const totalItems = selectedTab === DELETED_TAB_INDEX ? dataHidden?.totalItems || 0 : data?.totalItems || 0;

  const { data: statistics, isLoading: isLoadingStatistics } =
    userRole !== 'agency' ? useCGStatistics() : { data: null, isLoading: false };

  const queryClient = useQueryClient();

  const [openRegenerateDialog, setOpenRegenerateDialog] = useState<boolean>(false);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<any>(null);
  const [dealToRegenerate, setDealToRegenerate] = useState<any>(null);

  const { data: personsData, isLoading: isLoadingPersons } = usePersonsList();
  const { data: organizationsData, isLoading: isLoadingOrganizations } = useOrganizationsList();

  // Note: La logique de transition automatique des stages CG est maintenant gérée entièrement côté backend
  // Les transitions se font automatiquement lors de la sync Qonto et de la création des receipts CTVO

  useEffect(() => {
    if (sortColumn && sortOrder) {
      refetch();
    }
  }, [sortColumn, sortOrder]);

  useEffect(() => {
    if (searchQuery !== '') {
      setPage(0);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (!isLoading && cgListData) {
      setIsTableLoading(false); // Arrêter le loader une fois les données chargées
    }
  }, [isLoading, cgListData]);

  // Gérer l'ouverture automatique du drawer des commentaires externes depuis l'email
  useEffect(() => {
    if (openExternalCommentsId && !isLoading) {
      const targetId = parseInt(openExternalCommentsId);

      // Fonction pour rechercher l'ID dans un onglet spécifique
      const findDealInTab = async (tabIndex: number): Promise<boolean> => {
        try {
          // Changer vers l'onglet spécifique
          setSelectedTab(tabIndex);
          setPage(0); // Reset à la première page

          // Attendre que les données se chargent
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Récupérer les données de l'onglet via le filtre canonique cg_status
          const cgStatus = getCgStatusFilter(tabIndex);

          // Faire un appel API direct pour cet onglet
          const response = await makeAuthenticatedApiCall<{ cgList: any[] }>('get', '/api/cg', {
            params: {
              userPipedrive,
              userRole,
              page: 1,
              rowsPerPage: 1000, // Récupérer plus d'éléments pour la recherche
              cg_status: cgStatus,
              sortColumn: 'R.RegistrationID',
              sortOrder: 'DESC',
            },
          });

          const tabData = response.data?.cgList || [];
          const targetDeal = tabData.find((deal: any) => deal.RegistrationID === targetId);

          if (targetDeal) {
            console.log(`✅ Deal ${targetId} trouvé dans l'onglet ${tabIndex}`);
            return true;
          }

          return false;
        } catch (error) {
          console.error(`Erreur lors de la recherche dans l'onglet ${tabIndex}:`, error);
          return false;
        }
      };

      // Fonction pour ouvrir le drawer une fois l'onglet trouvé
      const openDrawer = () => {
        setTimeout(() => {
          const commentButton = document.querySelector(
            `[data-registration-id="${openExternalCommentsId}"] .external-comments-button`,
          );
          if (commentButton) {
            (commentButton as HTMLElement).click();
            console.log(`🎯 Drawer ouvert pour le deal ${openExternalCommentsId}`);

            // Nettoyer l'URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('openExternalComments');
            window.history.replaceState({}, '', newUrl.toString());
          } else {
            console.warn(`⚠️ Bouton commentaire non trouvé pour le deal ${openExternalCommentsId}`);
          }
        }, 1000);
      };

      // Rechercher dans l'onglet actuel d'abord
      const currentTabData = cgListData || [];
      const targetDealInCurrentTab = currentTabData.find((deal: any) => deal.RegistrationID === targetId);

      if (targetDealInCurrentTab) {
        console.log(`✅ Deal ${targetId} trouvé dans l'onglet actuel`);
        openDrawer();
        return;
      }

      // Si pas trouvé dans l'onglet actuel, rechercher dans tous les onglets
      console.log(`🔍 Deal ${targetId} non trouvé dans l'onglet actuel, recherche dans tous les onglets...`);

      const searchInAllTabs = async () => {
        // Définir les onglets à vérifier (exclure l'onglet supprimé)
        // 4 = « Réponse attendue » : un dossier ouvert depuis une notification
        // de commentaire y atterrit désormais, il doit être cherché là aussi.
        // 5 = « Réponse attendue » depuis l'insertion de l'onglet IA en 4.
        const tabsToCheck = [0, 1, 2, 3, 4, 5];

        for (const tabIndex of tabsToCheck) {
          if (tabIndex === selectedTab) continue; // Skip l'onglet actuel déjà vérifié

          console.log(`🔍 Recherche dans l'onglet ${tabIndex}...`);
          const found = await findDealInTab(tabIndex);

          if (found) {
            openDrawer();
            return;
          }
        }

        // Si pas trouvé dans aucun onglet
        console.warn(`❌ Deal ${targetId} non trouvé dans aucun onglet`);
        alert(`Le deal avec l'ID ${targetId} n'a pas été trouvé. Il est possible qu'il ait été supprimé ou déplacé.`);

        // Nettoyer l'URL même si pas trouvé
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('openExternalComments');
        window.history.replaceState({}, '', newUrl.toString());
      };

      searchInAllTabs();
    }
  }, [openExternalCommentsId, cgListData, isLoading, selectedTab, userPipedrive, userRole]);

  // Gérer la recherche automatique par plaque d'immatriculation depuis les mails
  useEffect(() => {
    if (autoSearchRegPlate && !isLoading && !autoSearchDone.current) {
      autoSearchDone.current = true;
      console.log(`🔍 Recherche automatique déclenchée pour la plaque: ${autoSearchRegPlate}`);

      // Pré-remplir le champ de recherche
      handleSearch(autoSearchRegPlate, refetch);

      // Attendre que la recherche se termine et que les résultats soient affichés
      setTimeout(() => {
        // Vérifier qu'il y a des résultats de recherche
        if (cgListData && cgListData.length > 0) {
          console.log(
            `✅ Recherche terminée avec ${cgListData.length} résultats pour la plaque: ${autoSearchRegPlate}`,
          );

          const firstResult = cgListData[0];
          if (firstResult) {
            console.log(`🎯 Tentative d'ouverture du drawer pour le deal: ${firstResult.RegistrationID}`);

            setTimeout(() => {
              const commentButton = document.querySelector(
                `[data-registration-id="${firstResult.RegistrationID}"] .external-comments-button`,
              );
              if (commentButton) {
                (commentButton as HTMLElement).click();
                console.log(`🎯 Drawer ouvert avec succès pour la plaque: ${autoSearchRegPlate}`);
              } else {
                console.warn(`⚠️ Bouton commentaire non trouvé pour le deal: ${firstResult.RegistrationID}`);
              }
              setIsAutoSearchLoading(false);
            }, 1000);
          } else {
            setIsAutoSearchLoading(false);
          }
        } else {
          console.warn(`⚠️ Aucun résultat trouvé pour la plaque: ${autoSearchRegPlate}`);
          setIsAutoSearchLoading(false);
        }

        // Nettoyer l'URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('autoSearch');
        window.history.replaceState({}, '', newUrl.toString());

        console.log(`✅ Recherche automatique terminée pour la plaque: ${autoSearchRegPlate}`);
      }, 2000);
    }
  }, [autoSearchRegPlate, isLoading, cgListData]);

  const handleDeleteCG = async (registrationId: number) => {
    const confirmDelete = window.confirm('Êtes-vous sûr de vouloir supprimer ce deal CG ?');
    if (confirmDelete) {
      setIsDeleting(true);
      try {
        await makeAuthenticatedApiCall('delete', `/api/cg/${registrationId}`);
        await refetch(); // Rafraîchir la liste après suppression
        alert('Le deal CG a été supprimé avec succès.');
      } catch (error) {
        console.error('Erreur lors de la suppression du deal CG:', error);
        alert('Une erreur est survenue lors de la suppression du deal CG.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleRestoreCG = async () => {
    if (!restoreTargetId) return;
    setIsDeleting(true);
    setRestoreDialogOpen(false);
    try {
      await makeAuthenticatedApiCall('patch', `/api/cg/${restoreTargetId}/visibility`, { visible: true });
      await queryClient.invalidateQueries({ queryKey: ['useCGList'] });
      await queryClient.refetchQueries({ queryKey: ['useCGList'] });
      alert('La CG a été restaurée avec succès.');
    } catch (error) {
      console.error('Erreur lors de la restauration de la CG:', error);
      alert('Une erreur est survenue lors de la restauration de la CG.');
    } finally {
      setIsDeleting(false);
      setRestoreTargetId(null);
    }
  };

  const handleOpenPaymentModal = async (invoice: string, price: number, clientId: string | null) => {
    setSelectedInvoice(invoice);

    // Récupérer les frais dynamiques pour ce client
    const skipcarFee = await (async () => {
      if (!clientId) {
        return 10; // Valeur par défaut
      }

      try {
        const response: any = await makeAuthenticatedApiCall('get', `/api/rfa/client-fees/${clientId}`);
        return response.data?.data?.cgFee ?? 10;
      } catch (error) {
        console.error('Erreur lors de la récupération des frais:', error);
        return 10; // Fallback en cas d'erreur
      }
    })();

    // La taxe carte grise est exonérée de TVA (0%), seule la commission Skipcar est soumise à 20% de TVA
    setPrice(price + skipcarFee * 1.2);
    setOpenPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setIsTableLoading(true);
    setSelectedTab(newValue);
    setPage(0); // Reset pagination to the first page when the tab changes
    if (newValue === DELETED_TAB_INDEX) {
      // Optionally refetch dataHidden if needed
      // refetchDataHidden();
    } else {
      // Refetch data for other tabs
      refetch();
    }
  };

  const handleChangePage = async (_event: React.MouseEvent | null, newPage: number) => {
    setIsTableLoading(true); // Démarrer le chargement
    setPage(newPage);
    // await refetch();
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setIsTableLoading(true);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // await refetch();
  };

  const handleOpenRegenerateDialog = (deal: any) => {
    setDealToRegenerate(deal);
    setOpenRegenerateDialog(true);
  };

  const handleCloseRegenerateDialog = () => {
    setOpenRegenerateDialog(false);
    setSelectedPerson(null);
    setSelectedOrganization(null);
  };

  const handleConfirmRegenerateInvoice = async () => {
    if (!dealToRegenerate) return;

    console.log('handleConfirmRegenerateInvoice / dealToRegenerate / ', dealToRegenerate);
    console.log('handleConfirmRegenerateInvoice / dealToRegenerate.TaxData / ', dealToRegenerate.TaxData);
    console.log('handleConfirmRegenerateInvoice / dealToRegenerate.TaxDataEstim / ', dealToRegenerate.TaxDataEstim);
    console.log(
      'handleConfirmRegenerateInvoice / dealToRegenerate.TaxData?.TaxeTotale / ',
      dealToRegenerate.TaxData?.TaxeTotale,
    );
    console.log(
      'handleConfirmRegenerateInvoice / dealToRegenerate.TaxDataEstim?.taxeTotale / ',
      dealToRegenerate.TaxDataEstim?.taxeTotale,
    );
    console.log(
      'handleConfirmRegenerateInvoice / final sellingPrice / ',
      dealToRegenerate.TaxData?.TaxeTotale ?? dealToRegenerate.TaxDataEstim?.taxeTotale,
    );

    setIsLoadingInvoice(true);
    try {
      await makeAuthenticatedApiCall('post', '/api/qonto/generate', {
        pipeline: 'f',
        deal: { ...dealToRegenerate, id: dealToRegenerate.deal_id },
        vat: 0,
        sellingPrice: dealToRegenerate.TaxData?.TaxeTotale ?? dealToRegenerate.TaxDataEstim?.taxeTotale,
        agencyData: { personID: selectedPerson?.id, agencyID: selectedOrganization?.id },
        regPlate: dealToRegenerate.RegPlate,
        // Régénération : on passe outre la garde d'idempotence côté backend.
        // L'ancienne row est marquée annulée localement ; l'annulation côté Qonto reste manuelle.
        forceRegenerate: true,
      });
    } catch (error: any) {
      console.error('Error generating invoice:', error);
      const message = error?.response?.data?.message;
      if (message) {
        alert(message);
      }
    }
    setIsLoadingInvoice(false);
    handleCloseRegenerateDialog();
  };

  if (error) return <Typography color="error">Erreur: {error.message}</Typography>;

  if (!isLoading && !isTableLoading && (!cgListData || cgListData.length === 0) && !searchQuery) {
    return (
      <Container maxWidth={false}>
        <Grid item xs={12} sx={{ marginBottom: 5 }}>
          <Typography align="center" mt={5} variant="body2">
            📅 Vous désirez passer sur une facturation à la quinzaine ou mensuelle :{' '}
            <Link target="_blank" href="mailto:skipcarcg@gmail.com">
              Contactez-nous
            </Link>{' '}
            ✉️
          </Typography>
        </Grid>

        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '200px' }}>
          <Card
            elevation={5}
            sx={{
              padding: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              marginTop: theme.spacing(0),
              width: '100%',
              maxWidth: '500px',

              background: 'linear-gradient(135deg, #fff8e1 0%, #ffe57f 100%)',
            }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{
                marginBottom: theme.spacing(1),
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              📊 Pas encore de carte grise
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{
                marginBottom: theme.spacing(2),
                color: 'black',
              }}
            >
              Les demandes de carte grise sont accessibles pour tout véhicule (même les véhicules vendus en
              intermédiation). Profitez en !
            </Typography>
            <Button
              variant="contained"
              color="primary"
              type="button"
              className="ActionButton"
              sx={{
                width: '350px',
                margin: '22px auto !important',
                backgroundColor: theme.palette.saffron.main,
                '&:hover': {
                  backgroundColor: theme.palette.saffron.dark,
                },
              }}
              onClick={() => {
                // Session FormCGIA vierge sans annuler l'analyse en cours
                // (consommé par FormCGIAv2 → handleNewSession).
                sessionStorage.setItem('form_cgia_new_session', '1');
              }}
              href="/form-cg"
            >
              🚗 Faire une demande de carte grise
            </Button>
          </Card>
        </Grid>
      </Container>
    );
  }

  async function handleInvoiceClick(dealId: number) {
    try {
      const response = await makeAuthenticatedApiCall<{ invoice_url_firebase: string }>(
        'get',
        `api/qonto/deals/f/${dealId}`,
      );
      if (response.data.invoice_url_firebase) {
        window.open(response.data.invoice_url_firebase, '_blank');
      } else {
        console.error('No invoice URL found');
        return false;
      }
    } catch (error) {
      console.error('Error getting invoice:', error);
    }
  }

  async function handleCGClick(RegistrationID: number) {
    navigate(`/form-cg?registrationId=${RegistrationID}`);
  }

  const onRefresh = async (changeCurrentTab?: number) => {
    setIsTableLoading(true);
    try {
      // Invalider les requêtes pour forcer un rechargement des données
      if (userRole === 'agency') {
        await queryClient.invalidateQueries({
          queryKey: ['useCGList', userPipedrive, userRole, page + 1, rowsPerPage],
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: ['useCGList'],
        });
      }

      // Si une recherche est en cours, relancer la recherche
      if (searchQuery && searchQuery.trim()) {
        await handleSearch(searchQuery, refetch);
      } else {
        // Sinon, recharger les données initiales
        await refetch();
      }

      if (changeCurrentTab) {
        setSelectedTab(changeCurrentTab);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleSort = (column: string) => {
    const isAsc = sortColumn === column && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortColumn(column);
  };

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}
        open={isAutoSearchLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" color="inherit">
          Chargement en cours...
        </Typography>
      </Backdrop>
      <Container maxWidth={false}>
        <Grid item xs={12}>
          <Typography align="center" mt={5} variant="body2">
            📅 Vous désirez passer sur une facturation à la quinzaine ou mensuelle :{' '}
            <Link target="_blank" href="mailto:skipcarcg@gmail.com">
              Contactez-nous
            </Link>{' '}
            ✉️
          </Typography>
        </Grid>

        {!currentUser?.data?.organization?.value && (
          <Typography variant="h6" sx={{ mt: 4, textAlign: 'center' }}>
            Il semblerait y avoir un problème dans la configuration de votre compte, veuillez nous contacter au 07 80 94
            97 37.
          </Typography>
        )}

        {currentUser?.data?.organization?.value && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <TextField
                  label="Rechercher"
                  className="inputElement"
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value, refetch)}
                  sx={{ marginRight: 0 }}
                />
                <Button
                  variant="contained"
                  onClick={() => handleSearch(searchQuery, refetch)}
                  className="dealActionButton"
                  sx={{
                    marginLeft: 1,
                    fontWeight: 900,
                  }}
                >
                  Rechercher
                </Button>
                {hasSearched && ( // Afficher le bouton si une recherche a été effectuée
                  <Button
                    className="secondaryDealActionButton"
                    variant="contained"
                    sx={{ marginLeft: 1 }}
                    color="error"
                    onClick={() => resetSearch(refetch)}
                  >
                    Revenir à la liste principale
                  </Button>
                )}
              </Box>
              <TablePagination
                className="custom-pagination"
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalItems || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={'Lignes par page'}
                labelDisplayedRows={({ from, to }) => `de ${from} à ${to}`}
              />
            </Box>
            {userRole !== 'agency' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 5,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  className="dealActionButton"
                  sx={{ fontWeight: 800 }}
                  onClick={() => onRefresh()}
                >
                  Rafraichir
                </Button>
                {/* Bouton de synchronisation séparé pour les tâches backend */}
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  className="secondaryDealActionButton"
                  sx={{ fontWeight: 600, marginLeft: 2 }}
                  onClick={async () => {
                    try {
                      setIsTableLoading(true);
                      console.log('🔄 Synchronisation manuelle des statuts CG...');
                      await makeAuthenticatedApiCall('post', '/api/cg/sync-status-transitions');
                      console.log('✅ Synchronisation terminée');
                      // Rafraîchir après sync
                      await onRefresh();
                    } catch (error) {
                      console.error('Erreur lors de la synchronisation:', error);
                    } finally {
                      setIsTableLoading(false);
                    }
                  }}
                >
                  Sync statuts
                </Button>
                {!isLoadingStatistics && (
                  <>
                    <Tooltip
                      title="Le nombre total de CG enregistrés qui sont visibles et ont un identifiant Pipedrive."
                      arrow
                      placement="top"
                    >
                      <Box>
                        <Typography variant="body1" textAlign={'center'}>
                          Total CG
                        </Typography>
                        <Typography variant="body2" textAlign={'center'}>
                          {statistics?.totalCG}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip
                      title="Le nombre total de CG qui ont été complétés, c'est-à-dire ceux qui ont atteint l'étape finale."
                      arrow
                      placement="top"
                    >
                      <Box>
                        <Typography variant="body1" textAlign={'center'}>
                          Total CG effectuées
                        </Typography>
                        <Typography variant="body2" textAlign={'center'}>
                          {statistics?.totalCompletedCG}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip
                      title="Le montant total des frais collectés par Skipcar pour les CG complétés. Calculé à partir du tarif par franchise configuré dans /admin/rfa (10€ par défaut)."
                      arrow
                      placement="top"
                    >
                      <Box>
                        <Typography variant="body1" textAlign={'center'}>
                          Recettes Skipcar
                        </Typography>
                        <Typography variant="body2" textAlign={'center'}>
                          {statistics?.totalFees} €
                        </Typography>
                      </Box>
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
            <Paper sx={{ marginTop: 5, padding: 0 }} elevation={3}>
              {userRole !== 'agency' && !hasSearched && (
                <Tabs
                  value={selectedTab}
                  className="DealsListTabs"
                  onChange={handleTabChange}
                  aria-label="CG List Tabs"
                >
                  {/* Onglets ISO avec la modale CGStageModal et les blocs AgencyCGList :
                      même libellés, même ordre, même statut canonique cg_status. */}
                  <Tab label="Tous" />
                  <Tab label="En attente de paiement" />
                  <Tab label="Paiement + pièces" />
                  <Tab label="Pièces à déposer" />
                  <Tab label="Pièces demandées par l’IA" />
                  <Tab label="Réponse attendue" />
                  <Tab label="En review Skipcar" />
                  <Tab label="Démarches ANTS en cours" />
                  <Tab label="Procédure effectuée" />
                  <Tab label="Rejetées" />
                  {userRole !== 'agency' && <Tab label={<span style={{ color: 'red' }}>Supprimées</span>} />}
                </Tabs>
              )}

              {isDocsTab && userRole !== 'agency' && (
                <Stack direction="row" spacing={1} sx={{ px: 2, pt: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="Toutes"
                    size="small"
                    color={docOriginFilter === null ? 'primary' : 'default'}
                    variant={docOriginFilter === null ? 'filled' : 'outlined'}
                    onClick={() => setDocOriginFilter(null)}
                  />
                  <Chip
                    label="Demandé par Skipcar"
                    size="small"
                    color={docOriginFilter === 'skipcar' ? 'primary' : 'default'}
                    variant={docOriginFilter === 'skipcar' ? 'filled' : 'outlined'}
                    onClick={() => setDocOriginFilter('skipcar')}
                  />
                  <Chip
                    label="Détecté au dépôt"
                    size="small"
                    color={docOriginFilter === 'analysis' ? 'primary' : 'default'}
                    variant={docOriginFilter === 'analysis' ? 'filled' : 'outlined'}
                    onClick={() => setDocOriginFilter('analysis')}
                  />
                </Stack>
              )}
              <Box
                sx={{
                  textAlign: 'center',
                  fontSize: 20,
                  fontWeight: 600,
                  background: '#EF8F35',
                  width: '100%',
                  fontFamily: 'Urbanist',
                  paddingTop: 2,
                  paddingBottom: 2,
                  color: 'white',
                }}
              >
                Liste des demandes de carte grise
              </Box>
              <Table stickyHeader>
                <TableHead className="stickyTableHead">
                  <TableRow sx={{ background: '#262626' }}>
                    {userRole !== 'agency' && (
                      <TableCell sx={{ ...stickyCellStyle, width: '30px', padding: 0, paddingLeft: 1 }}></TableCell>
                    )}
                    <TableCell
                      sx={{ ...stickyCellStyle, width: '90px', cursor: 'pointer' }}
                      onClick={() => handleSort('V.RegPlate')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Immat
                        {sortColumn === 'V.RegPlate' && (
                          <SortIcon>
                            {sortOrder === 'ASC' ? (
                              <ArrowUpwardIcon fontSize="small" />
                            ) : (
                              <ArrowDownwardIcon fontSize="small" />
                            )}
                          </SortIcon>
                        )}
                      </Box>
                    </TableCell>
                    {userRole !== 'agency' && (
                      <>
                        <TableCell
                          sx={{ ...stickyCellStyle, cursor: 'pointer' }}
                          onClick={() => handleSort('R.RegistrationID')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            ID
                            {sortColumn === 'R.RegistrationID' && (
                              <SortIcon>
                                {sortOrder === 'ASC' ? (
                                  <ArrowUpwardIcon fontSize="small" />
                                ) : (
                                  <ArrowDownwardIcon fontSize="small" />
                                )}
                              </SortIcon>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ ...stickyCellStyle, cursor: 'pointer' }}
                          onClick={() => handleSort('R.deal_id')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            Deal ID
                            {sortColumn === 'R.deal_id' && (
                              <SortIcon>
                                {sortOrder === 'ASC' ? (
                                  <ArrowUpwardIcon fontSize="small" />
                                ) : (
                                  <ArrowDownwardIcon fontSize="small" />
                                )}
                              </SortIcon>
                            )}
                          </Box>
                        </TableCell>
                      </>
                    )}
                    {userRole !== 'agency' && <TableCell sx={{ ...stickyCellStyle, width: '100px' }}>Prix</TableCell>}
                    {userRole !== 'agency' && <TableCell sx={{ ...stickyCellStyle, width: '75px' }}>Agence</TableCell>}
                    <TableCell sx={stickyCellStyle}>Vendeur / Acheteur</TableCell>
                    <TableCell sx={{ ...stickyCellStyle, cursor: 'pointer' }} onClick={() => handleSort('R.CreatedAt')}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Date de création
                        {sortColumn === 'R.CreatedAt' && (
                          <SortIcon>
                            {sortOrder === 'ASC' ? (
                              <ArrowUpwardIcon fontSize="small" />
                            ) : (
                              <ArrowDownwardIcon fontSize="small" />
                            )}
                          </SortIcon>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ ...stickyCellStyle, cursor: 'pointer' }}
                      onClick={() => handleSort('R.PurchaseDateTime')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Date de vente
                        {sortColumn === 'R.PurchaseDateTime' && (
                          <SortIcon>
                            {sortOrder === 'ASC' ? (
                              <ArrowUpwardIcon fontSize="small" />
                            ) : (
                              <ArrowDownwardIcon fontSize="small" />
                            )}
                          </SortIcon>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...stickyCellStyle, minWidth: '200px' }}>Commentaires</TableCell>
                    <TableCell sx={{ ...stickyCellStyle, textAlign: 'center' }}>Étape / Actions</TableCell>
                  </TableRow>
                </TableHead>
                {isTableLoading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {cgListData?.map((cg: any) => {
                      const currentStage = stages?.find((stage: any) => stage.id === cg.StageID);

                      return (
                        <TableRow key={`${cg.RegistrationID}-${cg.deal_id}`}>
                          {userRole !== 'agency' && (
                            <TableCell sx={{ width: '30px', padding: 0, paddingLeft: 1, borderBottom: '2px solid' }}>
                              {isDeleting ? (
                                <CircularProgress size={24} />
                              ) : selectedTab === DELETED_TAB_INDEX ? (
                                <RestoreFromTrashIcon
                                  sx={{ cursor: 'pointer', color: 'green' }}
                                  onClick={() => {
                                    setRestoreTargetId(cg.RegistrationID);
                                    setRestoreDialogOpen(true);
                                  }}
                                />
                              ) : (
                                <DeleteIcon
                                  sx={{ cursor: 'pointer', color: 'red' }}
                                  onClick={() => handleDeleteCG(cg.RegistrationID)}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell sx={{ minWidth: '90px', borderBottom: '2px solid' }}>
                            {cg.RegPlate}
                            {cg.CGReviewStatus === 'pending_god_review' && (
                              <Chip
                                label={
                                  // Ne mentionner l'IA que si le dossier a
                                  // réellement été analysé (ai_confidence
                                  // renseigné). Les CGs legacy passées
                                  // manuellement en Review via « Changer
                                  // l'étape CG » n'ont aucune analyse et
                                  // s'affichaient à tort comme CGIA
                                  // (retour François 2026-07, GG-386-VH).
                                  typeof cg.AIConfidence === 'number'
                                    ? `IA ${cg.AIConfidence}% · à reviewer`
                                    : 'À reviewer'
                                }
                                size="small"
                                color="warning"
                                onClick={() => navigate(`/cg/${cg.RegistrationID}/review`)}
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  fontSize: 11,
                                  height: 20,
                                  cursor: 'pointer',
                                  '&:hover': { opacity: 0.85 },
                                }}
                              />
                            )}
                            {/* `god_approved` = le dossier est parti à l'ANTS (cgStatus.ts :
                                god_approved ⇒ awaiting_misiv). « Validé Skipcar » décrivait
                                l'action passée du relecteur, pas l'état du dossier : François
                                l'a lu comme un échec de sa bascule (« il m'a mis validé skipcar
                                comme avant »). On affiche l'état, pas l'action. */}
                            {cg.CGReviewStatus === 'god_approved' && (
                              <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                label="Démarches ANTS en cours"
                                size="small"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/cg/${cg.RegistrationID}/review`);
                                }}
                                sx={{
                                  display: 'inline-flex',
                                  mt: 0.5,
                                  fontSize: 11,
                                  height: 20,
                                  cursor: 'pointer',
                                  '& .MuiChip-icon': { color: 'inherit', marginLeft: '4px' },
                                  '&:hover': { opacity: 0.85 },
                                }}
                              />
                            )}
                            {(cg.CGReviewStatus === 'awaiting_docs' ||
                              cg.CGReviewStatus === 'awaiting_payment_and_docs') && (
                              <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {(() => {
                                  const total = Number(cg.DocRequestsTotal ?? 0);
                                  const done = Number(cg.DocRequestsFulfilled ?? 0);
                                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                                  const baseLabel =
                                    cg.CGReviewStatus === 'awaiting_payment_and_docs'
                                      ? 'En attente paiement + pièces'
                                      : 'En attente de pièces';
                                  const label = total > 0 ? `${baseLabel} · ${done}/${total} (${pct}%)` : baseLabel;
                                  return (
                                    <Chip
                                      label={label}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/cg/${cg.RegistrationID}/review`);
                                      }}
                                      sx={{
                                        fontSize: 11,
                                        height: 20,
                                        alignSelf: 'flex-start',
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: '#FFF7E1' },
                                      }}
                                    />
                                  );
                                })()}
                                {userRole !== 'agency' && (
                                  <Chip
                                    icon={<NotificationsActiveIcon sx={{ fontSize: 14 }} />}
                                    label="Relancer l'agence"
                                    size="small"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await makeAuthenticatedApiCall('post', `/api/cg/${cg.RegistrationID}/relance`);
                                      } catch (err) {
                                        console.error('relance error:', err);
                                      }
                                    }}
                                    sx={{
                                      fontSize: 11,
                                      height: 20,
                                      alignSelf: 'flex-start',
                                      cursor: 'pointer',
                                      backgroundColor: '#FFF7E1',
                                      borderColor: '#fdd15f',
                                      '& .MuiChip-icon': { color: '#b86e00', marginLeft: '4px' },
                                      '&:hover': { backgroundColor: '#FFE9B4' },
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </TableCell>
                          {userRole !== 'agency' && (
                            <>
                              <TableCell style={{ width: '50px', borderBottom: '2px solid' }}>
                                {cg.RegistrationID}
                              </TableCell>
                              <TableCell style={{ width: '80px', borderBottom: '2px solid' }}>{cg.deal_id}</TableCell>
                            </>
                          )}
                          {userRole !== 'agency' && (
                            <TableCell
                              style={{
                                minWidth: '150px',
                                borderBottom: '2px solid',
                              }}
                            >
                              <CGPriceCell clientId={cg.ClientID} taxDataEstim={cg.TaxDataEstim} taxData={cg.TaxData} />
                            </TableCell>
                          )}
                          {userRole !== 'agency' && (
                            <TableCell sx={{ borderBottom: '2px solid' }}>{cg.AgencyName}</TableCell>
                          )}
                          <TableCell sx={{ borderBottom: '2px solid' }}>
                            {`${cg.SellerFirstName ?? ''} ${cg.SellerLastName ?? ''}`} {`${cg.SellerCompanyName ?? ''}`}
                            <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                            {`${cg.BuyerFirstName ?? ''} ${cg.BuyerLastName ?? ''}`} {`${cg.BuyerCompanyName ?? ''}`}
                            {cg.CoBuyerType && (
                              <Tooltip
                                title={
                                  cg.CoBuyerType === 'company'
                                    ? `${cg.CoBuyerCompanyName} / ${cg.CoBuyerSIRET}`
                                    : `${cg.CoBuyerFirstName} ${cg.CoBuyerLastName}`
                                }
                                placement="top"
                                key={`co-buyer-${cg.CoBuyerEntityID}-${cg.deal_id}`}
                              >
                                <Typography variant="body2" sx={{ fontSize: '12px', textAlign: 'left' }}>
                                  CoAcheteur
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderBottom: '2px solid' }}>
                            {formatDateToDDMMYYYY(cg.CreatedAt ?? cg.UpdatedAt) ?? ''}
                          </TableCell>
                          <TableCell sx={{ borderBottom: '2px solid' }}>
                            {formatDateToDDMMYYYY(cg.PurchaseDateTime ?? '')}
                          </TableCell>
                          <TableCell sx={{ minWidth: '200px', borderBottom: '2px solid' }}>
                            <CommentsManager
                              registrationId={cg.RegistrationID}
                              internalComments={cg.Comments || []}
                              userRole={userRole}
                              type="cg"
                              refetch={async () => {
                                await refetch();
                              }}
                              maxWidth={200}
                              regPlate={cg.RegPlate}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: '2px solid' }}>
                            <Stack direction="column" spacing={1} alignItems="center">
                              {cg.StageID === 46 && (
                                <Button
                                  sx={{
                                    fontSize: 14,
                                    background: '#FF6060',
                                    opacity: 1,

                                    fontWeight: 600,
                                    width: 152,
                                  }}
                                  className="dealActionButton"
                                  onClick={() =>
                                    handleOpenPaymentModal(
                                      cg.InvoiceNumber,
                                      cg?.TaxDataEstim?.taxeTotale ?? 0,
                                      cg.ClientID,
                                    )
                                  }
                                >
                                  Payer
                                </Button>
                              )}
                              {userRole === 'agency' && cg.StageID === 46 && (
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}
                                  >
                                    Référence pour le virement : {cg.InvoiceNumber}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}
                                  >
                                    Prix à payer :{' '}
                                    <CGAgencyPrice clientId={cg.ClientID} taxDataEstim={cg.TaxDataEstim} /> € TTC
                                  </Typography>
                                </>
                              )}
                              <Button
                                sx={{
                                  fontSize: 14,
                                  width: 152,
                                  display: cg.InvoiceStatus ? 'block' : 'none',
                                }}
                                className="secondaryDealActionButton"
                                variant="contained"
                                onClick={() => handleInvoiceClick(cg.deal_id)}
                              >
                                Voir la facture
                              </Button>

                              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                {cg.InvoiceNumber} {cg.InvoiceStatus && `/ ${t(`invoiceStatus.${cg.InvoiceStatus}`)}`}
                              </Typography>

                              <Button
                                sx={{
                                  fontSize: 14,
                                  width: 152,
                                }}
                                className="secondaryDealActionButton"
                                variant="contained"
                                onClick={() => handleCGClick(cg.RegistrationID)}
                              >
                                Voir la demande
                              </Button>
                              {/* Factures complémentaires (spécification CG agence v1.3, §13.8). */}
                              <ComplementBadge
                                cg={cg}
                                manage={['god', 'admin', 'arbitrator'].includes(userRole)}
                                onChanged={() => onRefresh()}
                              />
                              {userRole !== 'agency' && (
                                <SubmitCessionCertificate
                                  deal={cg}
                                  pipelineLetter="f"
                                  stageId={cg.StageID}
                                  userRole={userRole}
                                  onRefresh={onRefresh}
                                />
                              )}
                              <SubmitCTVO
                                deal={cg}
                                pipelineLetter="f"
                                stageId={cg.StageID}
                                userRole={userRole}
                                onRefresh={onRefresh}
                              />
                              {isLoadingStages || isLoading ? (
                                <CircularProgress />
                              ) : (
                                <>
                                  <Typography
                                    sx={{ marginTop: 1, fontSize: 16, fontWeight: '600' }}
                                    align="center"
                                    variant="body2"
                                    className="MuiTypographyTable"
                                  >
                                    {currentStage ? currentStage.name : 'Loading...'}
                                  </Typography>
                                  {userRole !== 'agency' && (
                                    <CGStageModal
                                      registrationId={cg.RegistrationID}
                                      stages={stages}
                                      initialStageId={cg.StageID}
                                      initialCgStatus={cg.cg_status ?? cg.CgStatus ?? null}
                                      initialCgReviewStatus={cg.CGReviewStatus ?? null}
                                      onRefresh={onRefresh}
                                    />
                                  )}
                                </>
                              )}
                              {userRole === 'god' && currentUser?.authUser?.uid === restrictedUserId && (
                                <Button
                                  sx={{
                                    fontSize: 14,
                                    width: 152,
                                  }}
                                  className="dealActionButton"
                                  variant="contained"
                                  onClick={() => handleOpenRegenerateDialog(cg)}
                                  disabled={isLoadingInvoice}
                                >
                                  {isLoadingInvoice ? <CircularProgress size={24} /> : 'Régénérer la facture'}
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
              </Table>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: 2,
                }}
              >
                <TablePagination
                  className="custom-pagination"
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={totalItems || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={'Lignes par page'}
                  labelDisplayedRows={({ from, to }) => `de ${from} à ${to}`}
                />
              </Box>
            </Paper>
          </>
        )}
      </Container>
      <PaymentModal
        open={openPaymentModal}
        onClose={handleClosePaymentModal}
        price={price}
        invoiceNumber={selectedInvoice}
      />
      <Dialog open={openRegenerateDialog} onClose={handleCloseRegenerateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Régénérer la facture</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={(personsData as any) || []}
            getOptionLabel={(option: any) => `${option.name} (ID: ${option.id})`}
            value={selectedPerson}
            className="inputElement"
            onChange={(_event, newValue) => setSelectedPerson(newValue)}
            loading={isLoadingPersons}
            renderInput={(params) => <TextField {...params} label="Personne" margin="dense" fullWidth />}
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {option.id} {option.email ? `- ${option.email}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            noOptionsText="Aucune personne trouvée"
            loadingText="Chargement..."
          />
          <Autocomplete
            options={(organizationsData as any) || []}
            getOptionLabel={(option: any) => `${option.name} (ID: ${option.id})`}
            value={selectedOrganization}
            onChange={(_event, newValue) => setSelectedOrganization(newValue)}
            loading={isLoadingOrganizations}
            className="inputElement"
            renderInput={(params) => <TextField {...params} label="Organisation (Agence)" margin="dense" fullWidth />}
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {option.id} {option.siret ? `- SIRET: ${option.siret}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            noOptionsText="Aucune organisation trouvée"
            loadingText="Chargement..."
          />
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={handleCloseRegenerateDialog} color="primary">
            Annuler
          </Button>
          <Button className="dealActionButton" onClick={handleConfirmRegenerateInvoice} color="primary">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Restaurer la CG</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir restaurer cette CG ? Elle sera de nouveau visible dans la liste active.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={() => setRestoreDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button className="dealActionButton" onClick={handleRestoreCG} color="primary">
            Restaurer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CGList;
